import api from './api';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      weekday: undefined,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return value;
  }
};

const formatCurrency = (value) => {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) return '£0.00';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount);
};

const getLineItems = (invoice) => {
  if (Array.isArray(invoice.lineItems)) return invoice.lineItems;
  if (Array.isArray(invoice.items)) return invoice.items;
  if (Array.isArray(invoice.metadata?.lineItems)) return invoice.metadata.lineItems;
  return [];
};

const getSafeAddress = (entity = {}) => {
  const parts = [
    entity.address || entity.addressLine1,
    [entity.city, entity.state || entity.region, entity.zipCode || entity.postcode].filter(Boolean).join(' '),
    entity.country
  ].filter(Boolean);
  return parts.join('<br/>') || '—';
};

const formatMoneyWithParens = (value) => {
  const amount = Number.parseFloat(value || 0);
  if (Number.isNaN(amount)) return '£0.00';
  const formatted = `£${Math.abs(amount).toFixed(2)}`;
  return amount < 0 ? `(${formatted})` : formatted;
};

const calculateTotalsFromLineItems = (lineItems = []) =>
  lineItems.reduce(
    (acc, item) => {
      const quantity = Number.parseFloat(item.quantity) || 0;
      const unitPrice = Number.parseFloat(item.unitPrice || item.price) || 0;
      const lineSubtotal = quantity * unitPrice;
      const discount = lineSubtotal * ((Number.parseFloat(item.discount) || 0) / 100);
      const afterDiscount = lineSubtotal - discount;
      const tax = afterDiscount * ((Number.parseFloat(item.taxRate) || 0) / 100);

      acc.subtotal += lineSubtotal;
      acc.tax += tax;
      return acc;
    },
    { subtotal: 0, tax: 0 }
  );

const shouldHydrateInvoice = (invoice = {}) => {
  const lineItems = getLineItems(invoice);
  const hasLineItems = Array.isArray(lineItems) && lineItems.length > 0;
  const hasSender = Boolean(invoice.sender);
  const hasReceiver = Boolean(invoice.receiver || invoice.customer || invoice.recipient || invoice.company || invoice.client);
  const hasNotes = Boolean(invoice.notes || invoice.terms);

  return !(hasLineItems && hasSender && hasReceiver && hasNotes);
};

const fetchInvoiceDetails = async (invoiceId, preferStaffEndpoint = false) => {
  if (!invoiceId) return null;

  const endpoints = preferStaffEndpoint
    ? [
        `/api/staff/invoices/${invoiceId}`,
        `/invoices/${invoiceId}`
      ]
    : [
        `/invoices/${invoiceId}`,
        `/api/staff/invoices/${invoiceId}`
      ];

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      const response = await api.get(endpoint);
      return (
        response.data?.invoice ||
        response.data?.data?.invoice ||
        response.data?.data ||
        response.data ||
        null
      );
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) {
    throw lastError;
  }

  return null;
};

const mergeInvoiceData = (baseInvoice = {}, hydratedInvoice = {}) => {
  const merged = {
    ...baseInvoice,
    ...hydratedInvoice
  };

  const hydratedLineItems = getLineItems(hydratedInvoice);
  if (hydratedLineItems.length > 0) {
    merged.lineItems = hydratedLineItems;
  }

  merged.sender = hydratedInvoice.sender || baseInvoice.sender;
  merged.receiver = hydratedInvoice.receiver || baseInvoice.receiver;
  merged.customer = hydratedInvoice.customer || baseInvoice.customer;
  merged.recipient = hydratedInvoice.recipient || baseInvoice.recipient;

  if (hydratedInvoice.metadata) {
    merged.metadata = {
      ...(baseInvoice.metadata || {}),
      ...hydratedInvoice.metadata
    };
  }

  merged.attachments =
    hydratedInvoice.attachments ||
    hydratedInvoice.metadata?.attachments ||
    baseInvoice.attachments ||
    baseInvoice.metadata?.attachments ||
    [];

  return merged;
};

const buildPrintableHtml = (invoice) => {
  const sender = invoice.sender || invoice.from || {};
  const receiver = invoice.receiver || invoice.to || invoice.customer || {};
  const lineItems = getLineItems(invoice);

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice ${invoice.invoiceNumber || 'Invoice'}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

        body {
          font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
          margin: 40px;
          color: #1f2937;
          background: #ffffff;
          line-height: 1.6;
        }

        .invoice-container {
          max-width: 860px;
          margin: 0 auto;
          background: white;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }

        .invoice-header {
          text-align: center;
          padding: 48px 32px 32px;
          background: linear-gradient(135deg, #f8fafc 0%, #ecfeff 100%);
          border-bottom: 1px solid #e2e8f0;
        }

        .invoice-title {
          font-size: 36px;
          font-weight: 300;
          color: #0f172a;
          margin: 0 0 8px 0;
          letter-spacing: 4px;
        }

        .title-line {
          width: 90px;
          height: 2px;
          background: #94a3b8;
          margin: 0 auto 24px auto;
        }

        .invoice-meta {
          display: flex;
          justify-content: center;
          gap: 32px;
          font-size: 13px;
          color: #475569;
          flex-wrap: wrap;
        }

        .meta-item {
          min-width: 140px;
        }

        .meta-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #94a3b8;
          margin-bottom: 4px;
        }

        .meta-value {
          font-weight: 600;
          color: #0f172a;
        }

        .company-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
          padding: 32px;
          background: #f8fafc;
        }

        .company-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 10px 25px -15px rgba(15, 23, 42, 0.3);
        }

        .company-card h3 {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #94a3b8;
          margin: 0 0 16px 0;
        }

        .company-name {
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
          margin-bottom: 4px;
        }

        .company-email {
          font-size: 14px;
          color: #475569;
          margin-bottom: 12px;
        }

        .company-address {
          font-size: 13px;
          color: #475569;
          line-height: 1.5;
        }

        table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          margin: 24px 0;
        }

        th {
          background: #0f172a;
          color: white;
          padding: 16px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          text-align: left;
        }

        td {
          padding: 16px;
          font-size: 14px;
          color: #0f172a;
          border-bottom: 1px solid #e2e8f0;
          background: white;
        }

        tr:nth-child(even) td {
          background: #f8fafc;
        }

        .totals-section {
          padding: 0 32px 40px;
        }

        .totals-card {
          background: #0f172a;
          color: white;
          border-radius: 20px;
          padding: 28px;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 20px;
        }

        .total-label {
          font-size: 11px;
          letter-spacing: 1px;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.65);
          margin-bottom: 6px;
        }

        .total-value {
          font-size: 22px;
          font-weight: 600;
        }

        .notes-card {
          margin: 0 32px 32px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          color: #475569;
          font-size: 14px;
        }

        .footer {
          text-align: center;
          padding: 32px;
          font-size: 12px;
          color: #64748b;
          background: #f1f5f9;
          border-top: 1px solid #e2e8f0;
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <div class="invoice-header">
          <div class="invoice-title">INVOICE</div>
          <div class="title-line"></div>
          <div class="invoice-meta">
            <div class="meta-item">
              <div class="meta-label">Invoice Number</div>
              <div class="meta-value">${invoice.invoiceNumber || invoice.customInvoiceNumber || '—'}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Issued</div>
              <div class="meta-value">${formatDate(invoice.createdAt || invoice.sentDate)}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Due Date</div>
              <div class="meta-value">${formatDate(invoice.dueDate)}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Status</div>
              <div class="meta-value">${(invoice.status || invoice.approvalStatus || 'pending').replace(/-/g, ' ').toUpperCase()}</div>
            </div>
          </div>
        </div>

        <div class="company-section">
          <div class="company-card">
            <h3>From</h3>
            <div class="company-name">${sender.companyName || [sender.firstName, sender.lastName].filter(Boolean).join(' ') || '—'}</div>
            <div class="company-email">${sender.email || '—'}</div>
            <div class="company-address">${getSafeAddress(sender)}</div>
          </div>
          <div class="company-card">
            <h3>To</h3>
            <div class="company-name">${receiver.companyName || [receiver.firstName, receiver.lastName].filter(Boolean).join(' ') || '—'}</div>
            <div class="company-email">${receiver.email || '—'}</div>
            <div class="company-address">${getSafeAddress(receiver)}</div>
          </div>
        </div>

        <div style="padding: 0 32px 32px;">
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Discount</th>
                <th>Tax</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${
                lineItems.length
                  ? lineItems.map((item) => {
                      const quantity = Number(item.quantity || 0);
                      const unitPrice = Number(item.unitPrice || item.price || 0);
                      const discount = Number(item.discount || 0);
                      const taxRate = Number(item.taxRate || 0);
                      const base = quantity * unitPrice;
                      const discountAmount = base * (discount / 100);
                      const taxableAmount = base - discountAmount;
                      const taxAmount = taxableAmount * (taxRate / 100);
                      const total = taxableAmount + taxAmount;
                      return `
                        <tr>
                          <td>${item.description || '—'}</td>
                          <td>${quantity || 0}</td>
                          <td>${formatCurrency(unitPrice)}</td>
                          <td>${discount ? `${discount}%` : '—'}</td>
                          <td>${taxRate ? `${taxRate}%` : '—'}</td>
                          <td>${formatCurrency(total)}</td>
                        </tr>
                      `;
                    }).join('')
                  : `<tr><td colspan="6">No line items</td></tr>`
              }
            </tbody>
          </table>
        </div>

        <div class="totals-section">
          <div class="totals-card">
            <div>
              <div class="total-label">Subtotal</div>
              <div class="total-value">${formatCurrency(invoice.subtotal || invoice.amount)}</div>
            </div>
            <div>
              <div class="total-label">Tax</div>
              <div class="total-value">${formatCurrency(invoice.tax)}</div>
            </div>
            ${invoice.discountAmount ? `
              <div>
                <div class="total-label">Discount</div>
                <div class="total-value">-${formatCurrency(invoice.discountAmount)}</div>
              </div>
            ` : ''}
            <div>
              <div class="total-label">Total</div>
              <div class="total-value">${formatCurrency(invoice.total || invoice.amount)}</div>
            </div>
          </div>
        </div>

        ${invoice.notes ? `
          <div class="notes-card">
            <div style="text-transform: uppercase; font-size: 11px; letter-spacing: 2px; color:#94a3b8; margin-bottom: 8px;">Notes</div>
            <div>${invoice.notes}</div>
          </div>
        ` : ''}

        ${invoice.terms ? `
          <div class="notes-card">
            <div style="text-transform: uppercase; font-size: 11px; letter-spacing: 2px; color:#94a3b8; margin-bottom: 8px;">Terms & Conditions</div>
            <div>${invoice.terms}</div>
          </div>
        ` : ''}

        <div class="footer">
          Thank you for choosing Receevit® • Receevit®.com
        </div>
      </div>
      <script>
        window.onload = function() {
          window.print();
          setTimeout(() => window.close(), 100);
        }
      </script>
    </body>
    </html>
  `;
};

const buildCsvContent = (invoice) => {
  const lineItems = getLineItems(invoice);
  const header = ['Description', 'Quantity', 'Unit Price', 'Discount', 'Tax', 'Total'];
  const rows = lineItems.map((item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || item.price || 0);
    const discount = Number(item.discount || 0);
    const taxRate = Number(item.taxRate || 0);
    const base = quantity * unitPrice;
    const discountAmount = base * (discount / 100);
    const taxableAmount = base - discountAmount;
    const taxAmount = taxableAmount * (taxRate / 100);
    const total = taxableAmount + taxAmount;
    return [
      item.description || '',
      quantity || 0,
      unitPrice.toFixed(2),
      discount || 0,
      taxRate || 0,
      total.toFixed(2)
    ];
  });

  const csv = [header, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
  return csv;
};

const triggerDownloadLink = (downloadUrl, filename) => {
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const tryApiDownload = async (invoice, normalizedFormat) => {
  const endpoint =
    normalizedFormat === 'pdf'
      ? `/invoices/${invoice.id}/download-pdf`
      : `/invoices/${invoice.id}/download-excel`;
  const filename = `invoice-${invoice.invoiceNumber || invoice.id}.${normalizedFormat === 'pdf' ? 'pdf' : 'csv'}`;

  const response = await api.get(endpoint, { responseType: 'blob' });
  if (response.status !== 200) {
    throw new Error('Unexpected response from invoice download endpoint');
  }

  const contentType = response.headers?.['content-type'] || '';
  if (contentType.includes('application/json')) {
    const payloadText =
      response.data instanceof Blob && response.data.text
        ? await response.data.text()
        : response.data;
    const payload =
      typeof payloadText === 'string' ? JSON.parse(payloadText) : payloadText;
    const downloadUrl = payload?.data?.downloadUrl || payload?.downloadUrl;
    if (downloadUrl) {
      triggerDownloadLink(downloadUrl, filename);
      return;
    }
    throw new Error(payload?.error || payload?.message || 'Failed to prepare download');
  }

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], {
          type: normalizedFormat === 'pdf' ? 'application/pdf' : 'text/csv'
        });

  const url = window.URL.createObjectURL(blob);
  triggerDownloadLink(url, filename);
  window.URL.revokeObjectURL(url);
};

const buildSentStylePrintableHtml = (invoice) => {
  const sender =
    invoice.sender ||
    invoice.from ||
    invoice.supplier ||
    invoice.metadata?.sender ||
    {};
  const receiver =
    invoice.receiver ||
    invoice.to ||
    invoice.customer ||
    invoice.recipient ||
    invoice.company ||
    invoice.client ||
    invoice.metadata?.receiver ||
    {};
  const lineItems = getLineItems(invoice);
  const totalsFromItems = calculateTotalsFromLineItems(lineItems);
  const rawSubtotal = Number.parseFloat(invoice.subtotal);
  const rawTax = Number.parseFloat(invoice.tax);
  const discountAmount = Number.parseFloat(invoice.discountAmount) || 0;
  const subtotal = Number.isFinite(rawSubtotal) ? rawSubtotal : totalsFromItems.subtotal;
  const tax = Number.isFinite(rawTax) ? rawTax : totalsFromItems.tax;
  const total =
    Number.parseFloat(invoice.total) ||
    Number.parseFloat(invoice.amount) ||
    Number.parseFloat(invoice.balanceDue) ||
    subtotal + tax - discountAmount;
  const invoiceDate =
    invoice.createdAt ||
    invoice.sentDate ||
    invoice.issueDate ||
    invoice.invoiceDate ||
    invoice.date ||
    invoice.updatedAt;
  const dueDate = invoice.dueDate || invoice.paymentDueDate;
  const status = (invoice.status || invoice.approvalStatus || '').replace(/_/g, ' ').trim();
  const poNumber = invoice.poNumber || invoice.po_number || invoice.purchaseOrderNumber;

  const lineItemsMarkup =
    lineItems.length > 0
      ? lineItems
          .map((item) => {
            const quantity = Number.parseFloat(item.quantity) || 0;
            const unitPrice = Number.parseFloat(item.unitPrice || item.price) || 0;
            const discount = Number.parseFloat(item.discount) || 0;
            const taxRate = Number.parseFloat(item.taxRate) || 0;
            const lineSubtotal = quantity * unitPrice;
            const discountValue = lineSubtotal * (discount / 100);
            const afterDiscount = lineSubtotal - discountValue;
            const taxValue = afterDiscount * (taxRate / 100);
            const lineTotal = afterDiscount + taxValue;
            return `
              <tr>
                <td>${item.description || '[Item description]'}</td>
                <td style="text-align: center;">${quantity}</td>
                <td class="amount">${formatMoneyWithParens(unitPrice)}</td>
                <td style="text-align: right;">${taxRate}%</td>
                <td style="text-align: right;">${discount}%</td>
                <td class="amount">${formatMoneyWithParens(lineTotal)}</td>
              </tr>
            `;
          })
          .join('')
      : `
        <tr>
          <td colspan="6">
            <div class="empty-state">
              <p>No line items found</p>
              <div class="subtitle">Add items to this invoice</div>
            </div>
          </td>
        </tr>
      `;

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice ${invoice.invoiceNumber || 'Invoice'}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        body {
          font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
          margin: 20px;
          color: #1f2937;
          background: #ffffff;
          line-height: 1.6;
        }
        .invoice-container {
          max-width: 960px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 24px;
          box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        .invoice-header {
          text-align: center;
          padding: 48px 32px 32px;
          background: #ffffff;
          border-bottom: 1px solid #f1f5f9;
        }
        .invoice-title {
          font-size: 44px;
          font-weight: 300;
          color: #111827;
          margin: 0 0 8px 0;
          letter-spacing: 3px;
        }
        .title-line {
          width: 90px;
          height: 2px;
          background: #94a3b8;
          margin: 0 auto 24px auto;
        }
        .invoice-meta {
          display: flex;
          justify-content: center;
          gap: 32px;
          font-size: 13px;
          color: #94a3b8;
          flex-wrap: wrap;
        }
        .meta-item { min-width: 140px; }
        .meta-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #94a3b8;
          margin-bottom: 4px;
        }
        .meta-value {
          font-weight: 600;
          color: #0f172a;
        }
        .status-badge {
          display: inline-flex;
          align-items: center;
          padding: 6px 16px;
          background: #e0e7ff;
          color: #4338ca;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 16px;
        }
        .company-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 24px;
          padding: 32px;
          background: #f8fafc;
        }
        .company-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 24px;
          box-shadow: none;
        }
        .company-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 2px;
          font-size: 11px;
        }
        .company-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #6366f1;
        }
        .company-card p {
          margin: 4px 0;
          color: #475569;
        }
        .line-items-section { padding: 32px; }
        .line-items-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }
        .line-items-header h3 {
          margin: 0;
          color: #1e293b;
          font-size: 16px;
          letter-spacing: 2px;
          text-transform: uppercase;
        }
        .line-items-table table {
          width: 100%;
          border-collapse: collapse;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        }
        .line-items-table th {
          background: #f8fafc;
          color: #475569;
          padding: 16px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 1px;
          text-align: left;
          border-bottom: 1px solid #e2e8f0;
        }
        .line-items-table td {
          padding: 16px;
          font-size: 14px;
          color: #1f2937;
          border-bottom: 1px solid #e2e8f0;
          background: white;
        }
        .line-items-table tr:nth-child(even) td { background: #f8fafc; }
        .line-items-table .amount {
          text-align: right;
          font-variant-numeric: tabular-nums;
        }
        .empty-state {
          text-align: center;
          padding: 32px 0;
          color: #94a3b8;
        }
        .empty-state .subtitle {
          font-size: 12px;
          color: #cbd5f5;
        }
        .totals-section { padding: 0 32px 40px; }
        .totals-card {
          background: #f8fafc;
          border-radius: 20px;
          border: 1px solid #e2e8f0;
          padding: 24px;
          box-shadow: none;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
          font-size: 15px;
          color: #0f172a;
        }
        .grand-total .total-amount {
          font-size: 26px;
          font-weight: 700;
          color: #0f172a;
        }
        .notes-section { padding: 0 32px 40px; }
        .note-card {
          background: #f8fafc;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          padding: 24px;
          margin-bottom: 16px;
        }
        .note-title {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          margin: 0 0 12px 0;
        }
        .note-content {
          margin: 0;
          font-size: 13px;
          color: #475569;
          line-height: 1.6;
        }
        .no-print {
          text-align: center;
          padding: 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
        }
        .no-print button {
          background: #475569;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          cursor: pointer;
          margin: 0 8px;
          font-size: 14px;
          font-weight: 500;
        }
        .no-print button:hover { background: #334155; }
        @page { margin: 0.5in !important; size: A4 !important; }
        @media print {
          * {
            -webkit-print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          html { background: white !important; }
          .no-print { display: none !important; }
          .invoice-container {
            max-width: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 !important;
            padding: 0 !重要;
            border: none !important;
          }
          .invoice-header { background: white !important; border-bottom: 1px solid #e2e8f0; }
          .company-section,
          .line-items-table,
          .totals-section { page-break-inside: avoid; }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <div class="invoice-header">
          <h1 class="invoice-title">INVOICE</h1>
          <div class="title-line"></div>
          <div class="invoice-meta">
            <div class="meta-item">
              <div class="meta-label">Invoice Date</div>
              <div class="meta-value">${invoiceDate ? formatDate(invoiceDate) : '—'}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Invoice Number</div>
              <div class="meta-value">${invoice.invoiceNumber || '—'}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Due Date</div>
              <div class="meta-value">${dueDate ? formatDate(dueDate) : '—'}</div>
            </div>
          </div>
          ${status ? `<div class="status-badge">${status}</div>` : ''}
          ${
            poNumber
              ? `<div style="margin-top: 12px; text-align: center; font-size: 12px; color: #64748b;"><strong>PO Number:</strong> ${poNumber}</div>`
              : ''
          }
        </div>
        <div class="company-section">
          <div class="company-card">
            <div class="company-header">
              <div class="company-dot"></div>
              <h3>From</h3>
            </div>
            <p><strong>${sender.companyName || `${sender.firstName || ''} ${sender.lastName || ''}`.trim() || '[Company Name]'}</strong></p>
            <p>${sender.firstName || ''} ${sender.lastName || ''}</p>
            ${sender.address ? `<p>${sender.address}</p>` : ''}
            ${
              sender.city || sender.state || sender.zipCode
                ? `<p>${[sender.city, sender.state, sender.zipCode].filter(Boolean).join(', ')}</p>`
                : ''
            }
            ${sender.country ? `<p>${sender.country}</p>` : ''}
            <p>${sender.email || '[Email]'}</p>
          </div>
          <div class="company-card">
            <div class="company-header">
              <div class="company-dot"></div>
              <h3>To</h3>
            </div>
            <p><strong>${receiver.companyName || `${receiver.firstName || ''} ${receiver.lastName || ''}`.trim() || '[Company Name]'}</strong></p>
            <p>${receiver.firstName || ''} ${receiver.lastName || ''}</p>
            ${receiver.address ? `<p>${receiver.address}</p>` : ''}
            ${
              receiver.city || receiver.state || receiver.zipCode
                ? `<p>${[receiver.city, receiver.state, receiver.zipCode].filter(Boolean).join(', ')}</p>`
                : ''
            }
            ${receiver.country ? `<p>${receiver.country}</p>` : ''}
            <p>${receiver.email || '[Email]'}</p>
          </div>
        </div>
        <div class="line-items-section">
          <div class="line-items-header">
            <div class="company-dot"></div>
            <h3>Line Items</h3>
          </div>
          <div class="line-items-table">
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th style="text-align: center;">Qty</th>
                  <th style="text-align: right;">Unit Price</th>
                  <th style="text-align: right;">Tax %</th>
                  <th style="text-align: right;">Discount %</th>
                  <th style="text-align: right;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${lineItemsMarkup}
              </tbody>
            </table>
          </div>
        </div>
        <div class="totals-section">
          <div class="totals-card">
            <div class="total-row">
              <span style="color: #64748b;">Subtotal:</span>
              <span style="font-weight: 500;">${formatMoneyWithParens(subtotal)}</span>
            </div>
            <div class="total-row">
              <span style="color: #64748b;">Tax:</span>
              <span style="font-weight: 500;">${formatMoneyWithParens(tax)}</span>
            </div>
            ${
              discountAmount > 0
                ? `
                  <div class="total-row" style="background: #f9f9f9; margin: 8px -16px; padding: 8px 16px; border-top: 1px dashed #999; border-bottom: 1px dashed #999;">
                    <span style="color: #555; font-style: italic;">
                      ✨ Early Payment Discount
                      ${
                        invoice.discountSourceInvoice?.invoiceNumber
                          ? `<br><span style="font-size: 10px; font-style: normal;">(from ${invoice.discountSourceInvoice.invoiceNumber})</span>`
                          : ''
                      }:
                    </span>
                    <span style="color: #555; font-style: italic; font-weight: 500;">-${formatMoneyWithParens(discountAmount)}</span>
                  </div>
                `
                : ''
            }
            <div class="total-row grand-total">
              <span>Total:</span>
              <span class="total-amount">${formatMoneyWithParens(total)}</span>
            </div>
          </div>
        </div>
        ${
          invoice.notes
            ? `
              <div class="notes-section">
                <div class="note-card">
                  <div class="note-title">Notes:</div>
                  <p class="note-content">${invoice.notes}</p>
                </div>
              </div>
            `
            : ''
        }
        ${
          invoice.terms
            ? `
              <div class="notes-section">
                <div class="note-card">
                  <div class="note-title">Terms & Conditions:</div>
                  <p class="note-content">${invoice.terms}</p>
                </div>
              </div>
            `
            : ''
        }
        <div class="no-print">
          <p>This invoice can be printed or saved as PDF using your browser's print function.</p>
          <button onclick="window.print()">Print / Save as PDF</button>
          <button onclick="window.close()">Close</button>
        </div>
      </div>
    </body>
    </html>
  `;
};

const fallbackDownload = (invoice, normalizedFormat, options = {}) => {
  if (normalizedFormat === 'pdf') {
    const printable = options.forcePrintablePreview
      ? buildSentStylePrintableHtml(invoice)
      : buildPrintableHtml(invoice);
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Unable to open print window');
    }
    printWindow.document.write(printable);
    return;
  }

  const csvContent = buildCsvContent(invoice);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  triggerDownloadLink(url, `invoice-${invoice.invoiceNumber || 'invoice'}.csv`);
  URL.revokeObjectURL(url);
};

export const downloadInvoice = async (invoice, format = 'pdf', options = {}) => {
  if (!invoice || !invoice.id) {
    throw new Error('Invoice is required for download');
  }

  const normalizedFormat = format === 'pdf' ? 'pdf' : 'csv';
  const { setLoading } = options;
  let invoicePayload = invoice;

  try {
    if (setLoading) setLoading(true);

    if (options.hydrate !== false && shouldHydrateInvoice(invoice)) {
      try {
        const fetchedInvoice = await fetchInvoiceDetails(
          invoice.id,
          options.preferStaffEndpoint
        );
        if (fetchedInvoice) {
          invoicePayload = mergeInvoiceData(invoice, fetchedInvoice);
        }
      } catch (hydrationError) {
        console.warn('Invoice hydration failed, continuing with base invoice', hydrationError);
      }
    }

    if (options.forcePrintablePreview && normalizedFormat === 'pdf') {
      fallbackDownload(invoicePayload, normalizedFormat, options);
      return;
    }

    await tryApiDownload(invoicePayload, normalizedFormat);
  } catch (apiError) {
    console.warn('Invoice API download failed, using fallback renderer', apiError);
    fallbackDownload(invoicePayload, normalizedFormat, options);
  } finally {
    if (setLoading) setLoading(false);
  }
};

